// Whether a record the dealer is looking at actually exists in Salesforce.
//
// Every table the portal edits is a Heroku Connect mirror. Connect pushes a
// change to Salesforce after the API has already answered, so a write that
// Salesforce refuses fails long after the dealer saw "Saved". The row stays in
// Postgres, the portal keeps reading it back, and nothing in the UI ever says
// that Salesforce does not have it. Production is currently holding 834 boats,
// 86 memberships and 44 contacts in that state.
//
// Connect records the outcome on the row itself, so the portal can just read
// it: `_hc_lastop` is the last operation's result and `_hc_err` carries
// Salesforce's own message. Both come back from the API already, because the
// endpoints select whole rows.

// A row Salesforce has accepted has an sfid. Anything else is either on its way
// or stuck.
export function syncState(record) {
  if (!record) return "unknown";
  if (record._hc_lastop === "FAILED") return "failed";
  if (!record.sfid) return "pending";
  return "synced";
}

// Salesforce's own explanation, dug out of the JSON Connect stores.
// Example: {"op": "INSERT", "src": "SFDC", "msg": "Required fields are missing: [Account__c]"}
export function syncErrorMessage(record) {
  const raw = record && record._hc_err;
  if (!raw) return null;
  if (typeof raw === "object") return raw.msg || null;

  try {
    const parsed = JSON.parse(raw);
    return parsed && parsed.msg ? parsed.msg : String(raw);
  } catch (error) {
    // Older rows store a bare string rather than JSON.
    return String(raw);
  }
}

// One line for the dealer, or null when the record is fine. Deliberately says
// what it means for them rather than quoting Heroku Connect at them, with
// Salesforce's message appended when there is one, because that is the part
// whoever fixes it in Salesforce will need.
export function syncWarning(record) {
  const state = syncState(record);
  if (state === "synced" || state === "unknown") return null;

  if (state === "failed") {
    const reason = syncErrorMessage(record);
    const lead = "Salesforce rejected this record, so it is not there yet and edits here will not reach it.";
    return reason ? `${lead} Salesforce said: ${reason}` : lead;
  }

  return "This record has not reached Salesforce yet. It should appear shortly; if it does not, it was rejected.";
}

// Short form for a badge next to the record's heading.
export function syncBadge(record) {
  const state = syncState(record);
  if (state === "failed") return { text: "Not in Salesforce", variant: "danger" };
  if (state === "pending") return { text: "Syncing", variant: "warning" };
  return null;
}
